import { useEffect, useState } from 'react';
import { useRoute, useLocation, useSearch, Link } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Pencil, Trash2, FileText, Calendar, Eye, CalendarPlus, Lock } from 'lucide-react';
import { customerService } from '@/services/api/customer.service';
import { Button } from '@/components/ui/button';
import { iconButtonVariants } from '@/components/ui/icon-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { CustomerBookingsWithNotes } from '@/components/sessions/CustomerBookingsWithNotes';
import { useAuth } from '@/contexts/auth-context';
import { useIsTherapyPractice } from '@/hooks/use-sessions';
import { useToast } from '@/hooks/use-toast';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import { CustomerFormDialog } from '@/components/customers/CustomerFormDialog';
import type { BookingStatus } from '@/lib/status-colors';

export default function AdminCustomerDetailPage() {
  const { t } = useTranslation(['customers', 'common', 'bookings']);
  const [, params] = useRoute('/admin/customers/:id');
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [isEditOpen, setIsEditOpen] = useState(false);

  const customerId = (params as { id?: string })?.id || null;
  const isAdmin = user?.role === 'admin';
  const { enabled: isTherapist } = useIsTherapyPractice();
  // Older links to the record opened a tab here (?tab=sessions); it has its own page now.
  const wantsRecord = new URLSearchParams(useSearch()).get('tab') === 'sessions';
  useEffect(() => {
    if (wantsRecord && isTherapist && customerId) setLocation(`/admin/sessions/clients/${customerId}`, { replace: true });
  }, [wantsRecord, isTherapist, customerId, setLocation]);

  const { data: customer, isLoading, isError } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => customerService.getCustomer(customerId!),
    enabled: !!customerId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number | string) => customerService.deleteCustomer(id),
    onSuccess: () => {
      toast({ title: t('customers:toasts.deleteSuccess') });
      queryClient.invalidateQueries({ queryKey: ['admin-customers'] });
      setLocation('/admin/customers');
    },
    onError: () => {
      toast({ title: t('customers:toasts.deleteError'), variant: 'destructive' });
    },
  });

  const handleDelete = () => {
    if (!customer) return;
    const confirmed = window.confirm(
      t('customers:confirmations.delete', { name: customer.fullName }) as string,
    );
    if (!confirmed) return;
    deleteMutation.mutate(customer.publicId ?? customer.id);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-5 w-40" />
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-4 w-80" />
            <Skeleton className="h-4 w-48" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-0">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="flex gap-4 px-4 py-3 border-b border-line last:border-0"
              >
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-20 ml-auto" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isError || !customer) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation('/admin/customers')}
          iconLeft={<ChevronLeft />}
        >
          {t('customers:detail.back')}
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-soft">
              {t('customers:detail.notFound')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const bookings = customer.bookings ?? [];
  const dateFormats = getDateFormats();
  const locale = getDateLocale();

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLocation('/admin/customers')}
        iconLeft={<ChevronLeft />}
        aria-label={t('customers:detail.backAriaLabel')}
      >
        {t('customers:detail.back')}
      </Button>

      {/* Header card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl text-ink sm:text-h1">
                {customer.fullName}
              </h1>
              <p className="text-body text-soft">
                <span>{customer.email}</span>
                {customer.phone && (
                  <>
                    <span className="mx-2 text-soft" aria-hidden="true">·</span>
                    <span>{customer.phone}</span>
                  </>
                )}
              </p>
              <p className="text-body-sm text-soft">
                {t('customers:detail.visitSummary', {
                  count: customer.totalVisits,
                  lastVisit: customer.lastVisitAt
                    ? format(parseISO(customer.lastVisitAt), dateFormats.medium, { locale })
                    : t('customers:detail.never'),
                })}
              </p>
              {isTherapist && (
                <span className="!mt-2 inline-flex items-center gap-1.5 rounded-pill bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('sessions:customerTab.recordChip')}
                </span>
              )}
            </div>

            {/* Phones: one full-width primary action, edit and delete as icon buttons. */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                className="flex-1 sm:flex-none"
                onClick={() =>
                  setLocation(
                    `/admin/bookings/new?customer_id=${customer.publicId ?? customer.id}`,
                  )
                }
                iconLeft={<CalendarPlus className="h-4 w-4" />}
                aria-label={t('customers:actions.newBookingAriaLabel', { name: customer.fullName })}
                data-testid="customer-new-booking-button"
              >
                {t('customers:actions.newBooking')}
              </Button>

              <Button
                variant="outline"
                onClick={() => setIsEditOpen(true)}
                iconLeft={<Pencil className="h-4 w-4" />}
                aria-label={t('common:actions.edit')}
                className="px-3 sm:px-4"
              >
                <span className="hidden sm:inline">{t('common:actions.edit')}</span>
              </Button>

              {isAdmin && (
                <>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    iconLeft={<Trash2 className="h-4 w-4" />}
                    aria-label={t('customers:actions.deleteAriaLabel', { name: customer.fullName })}
                    className="hidden sm:inline-flex"
                  >
                    {t('common:actions.delete')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    iconLeft={<Trash2 className="h-4 w-4 text-danger" />}
                    aria-label={t('customers:actions.deleteAriaLabel', { name: customer.fullName })}
                    className="px-3 sm:hidden"
                  />
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      {customer.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('customers:detail.notes')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-body text-ink bg-surface-2 rounded-lg p-3 whitespace-pre-wrap">
              {customer.notes}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Bookings. Therapists see each booking's session note and a link to the
          client's session record (its own page under Session notes). */}
      {isTherapist ? (
        <Card className="overflow-hidden">
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('customers:detail.bookingHistory')}
              </CardTitle>
              <Link
                href={`/admin/sessions/clients/${customer.publicId ?? customer.id}`}
                className="inline-flex min-h-touch-target items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900"
              >
                <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                {t('sessions:client.openRecord')}
              </Link>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <CustomerBookingsWithNotes bookings={bookings} />
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('customers:detail.bookingHistory')}
              </CardTitle>
              {bookings.length > 0 && (
                <Badge tone="neutral">
                  {bookings.length}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {bookings.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Calendar className="h-8 w-8 mx-auto mb-3 text-soft" />
                <p className="text-soft">
                  {t('customers:detail.noBookings')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead>
                    <tr className="border-b border-line bg-surface-2/60">
                      <th
                        scope="col"
                        className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap"
                      >
                        {t('customers:bookingTable.date')}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden sm:table-cell"
                      >
                        {t('customers:bookingTable.service')}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell"
                      >
                        {t('customers:bookingTable.provider')}
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap"
                      >
                        {t('customers:bookingTable.status')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right">
                        <span className="sr-only">{t('customers:bookingTable.viewLabel')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => {
                      const bookingId = booking.publicId ?? booking.id;
                      const status = booking.status as BookingStatus;
                      return (
                        <tr
                          key={booking.id}
                          className="border-b border-line last:border-0 hover:bg-surface-2 transition-colors"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-ink">
                              {booking.startTime ? (
                                format(parseISO(booking.startTime), dateFormats.medium, { locale })
                              ) : (
                                <span className="text-soft">—</span>
                              )}
                            </p>
                            <p className="text-body-xs text-soft sm:hidden">
                              {booking.serviceName}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-ink hidden sm:table-cell">
                            {booking.serviceName ?? '—'}
                          </td>
                          <td className="px-4 py-3 text-ink hidden md:table-cell">
                            {booking.providerName ?? '—'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={status}>
                              {t(`bookings:status.${status === 'no_show' ? 'noShow' : status}`)}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              href={`/admin/bookings/${bookingId}`}
                              aria-label={t('customers:bookingTable.viewAriaLabel', {
                                code: booking.bookingCode ?? booking.id,
                              })}
                              className={iconButtonVariants({ variant: 'subtle', size: 'lg' })}
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <CustomerFormDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        customer={customer}
      />
    </div>
  );
}
